import { FIELD_TYPES } from '@/constants/vehicleFieldConfig'

/**
 * Structured enhancement fields stay structured.
 * getFieldInputType() defaults unknown fields to "text", so it cannot decide this boundary.
 */
const STRUCTURED_ENHANCEMENT_FIELDS = new Set([
  'features',
  'tech_specs',
  'dealer_photos',
  'stock_photos',
  'conditions',
])

const IDENTITY_OR_METADATA_FIELDS = new Set(['__prefill', 'vin', 'jazelId', 'lock'])

const NUMERIC_FIELDS = new Set<string>(FIELD_TYPES.number)

const CLEAN_NUMERIC_STRING = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/

export type SanitizedEnhancementPrefill = {
  values: Record<string, unknown>
  hasUsableEnhancementContent: boolean
  usableCount: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseCleanNumericString(value: string): number | undefined {
  const trimmed = value.trim()
  if (!CLEAN_NUMERIC_STRING.test(trimmed)) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

function sanitizeField(field: string, raw: unknown): { include: boolean; value: unknown } {
  if (STRUCTURED_ENHANCEMENT_FIELDS.has(field)) {
    return { include: true, value: raw }
  }

  if (NUMERIC_FIELDS.has(field)) {
    if (typeof raw === 'number') {
      return Number.isFinite(raw) ? { include: true, value: raw } : { include: false, value: undefined }
    }
    if (raw === null) return { include: true, value: null }
    if (typeof raw === 'string') {
      const parsed = parseCleanNumericString(raw)
      return parsed === undefined ? { include: false, value: undefined } : { include: true, value: parsed }
    }
    return { include: false, value: undefined }
  }

  if (raw === null || typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return { include: true, value: raw }
  }

  return { include: false, value: undefined }
}

/** Whether a sanitized enhancement value can be used as a candidate. */
export function isUsableEnhancementValue(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string' && value.trim() === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  if (isRecord(value) && Object.keys(value).length === 0) return false
  return true
}

export function sanitizeAddVehicleEnhancementPrefill(payload: unknown): SanitizedEnhancementPrefill {
  if (!isRecord(payload)) {
    return { values: {}, hasUsableEnhancementContent: false, usableCount: 0 }
  }

  const values: Record<string, unknown> = {}
  for (const [field, raw] of Object.entries(payload)) {
    if (field === '__prefill') continue
    const sanitized = sanitizeField(field, raw)
    if (sanitized.include) values[field] = sanitized.value
  }

  let usableCount = 0
  for (const [field, value] of Object.entries(values)) {
    if (IDENTITY_OR_METADATA_FIELDS.has(field)) continue
    if (isUsableEnhancementValue(value)) usableCount += 1
  }

  return {
    values,
    hasUsableEnhancementContent: usableCount > 0,
    usableCount,
  }
}
